import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, isSupported, deleteToken } from 'firebase/messaging';

// Firebase configuration (from fcm-test-client.html)
export const firebaseConfig = {
    apiKey: "AIzaSyDO76kBHjmZGKX9m-cHUvf6m_9u1XAmGk8",
    authDomain: "chronox-web-app.firebaseapp.com",
    projectId: "chronox-web-app",
    storageBucket: "chronox-web-app.firebasestorage.app",
    messagingSenderId: "315751167870",
    appId: "1:315751167870:web:6929ddab779b67743f252f",
    measurementId: "G-MBJBDRS0HS"
};

// VAPID key for FCM
export const VAPID_KEY = process.env.REACT_APP_VAPID_KEY ?? "";

// Initialize Firebase App
let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let messagingInitialized = false;
let initializationInProgress = false;

/**
 * Initialize Firebase App (can be called immediately)
 */
export const initializeFirebaseApp = (): FirebaseApp => {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  return app;
};

/**
 * Initialize Firebase Messaging (must be called after service worker is registered)
 */
export const initializeFirebaseMessaging = async (): Promise<Messaging | null> => {
  // Prevent multiple simultaneous initializations
  if (messagingInitialized && messaging) {
    console.log('[FCM] Firebase Messaging already initialized');
    return messaging;
  }

  if (initializationInProgress) {
    console.log('[FCM] Firebase Messaging initialization in progress, waiting...');
    // Wait for initialization to complete
    let waitCount = 0;
    while (initializationInProgress && waitCount < 20) {
      await new Promise(resolve => setTimeout(resolve, 100));
      waitCount++;
    }
    return messaging;
  }

  initializationInProgress = true;

  try {
    // Check if messaging is supported
    const supported = await isSupported();
    if (!supported) {
      console.error('[FCM] Firebase Messaging is not supported in this browser');
      initializationInProgress = false;
      return null;
    }

    // Ensure Firebase App is initialized
    if (!app) {
      initializeFirebaseApp();
    }

    // Get messaging instance
    messaging = getMessaging(app!);
    messagingInitialized = true;
    initializationInProgress = false;

    console.log('[FCM] Firebase Messaging initialized successfully');
    return messaging;
  } catch (error) {
    console.error('[FCM] Error initializing Firebase Messaging:', error);
    initializationInProgress = false;
    return null;
  }
};

/**
 * Delete existing FCM token (to force generation of new token)
 * Note: This may fail if service worker has issues, but we'll continue anyway
 */
export const deleteFCMToken = async (serviceWorkerRegistration?: ServiceWorkerRegistration | null): Promise<boolean> => {
  try {
    if (!messaging) {
      console.log('[FCM] No messaging instance, nothing to delete');
      return true;
    }

    console.log('[FCM] Attempting to delete existing FCM token...');
    
    try {
      // Delete token - Firebase will handle it even if token doesn't exist
      // This may fail due to service worker scope issues, but that's okay
      await deleteToken(messaging);
      console.log('[FCM] Token deleted successfully');
      return true;
    } catch (deleteError: any) {
      // Handle various error cases
      const errorCode = deleteError?.code || '';
      const errorMessage = deleteError?.message || '';
      
      // These errors mean token doesn't exist or already deleted - that's fine
      if (errorCode === 'messaging/token-not-found' || 
          errorCode === 'messaging/invalid-argument' ||
          errorMessage.includes('token-not-found')) {
        console.log('[FCM] No token to delete (already deleted or never existed)');
        return true;
      }
      
      // Service worker registration errors - we'll skip deletion and continue
      // This is common and safe - Firebase tokens are designed to be reused
      if (errorMessage.includes('service worker') || 
          errorMessage.includes('ServiceWorker') ||
          errorMessage.includes('failed-service-worker-registration') ||
          errorMessage.includes('firebase-cloud-messaging-push-scope') ||
          errorCode === 'messaging/failed-service-worker-registration') {
        console.log('[FCM] Service worker scope issue during token deletion - this is safe to ignore');
        console.log('[FCM] Firebase tokens are designed to be reused, continuing without deletion');
        console.log('[FCM] The existing token will be reused (this is normal behavior)');
        return true; // Continue anyway - token reuse is expected
      }
      
      // Other errors - log but continue
      console.log('[FCM] Error deleting token:', errorMessage);
      console.log('[FCM] Will continue with token generation anyway');
      return true; // Continue anyway - try to generate new token
    }
  } catch (error: any) {
    console.log('[FCM] Error in deleteFCMToken:', error?.message);
    // Don't fail - continue to generate new token anyway
    return true;
  }
};

/**
 * Get FCM token
 * If a ServiceWorkerRegistration is provided, it will be used so that background
 * notifications can work. If not provided, Firebase will try to get token without it.
 * Note: Some browsers may require a service worker for token generation.
 */
export const getFCMToken = async (serviceWorkerRegistration?: ServiceWorkerRegistration | null, forceNewToken: boolean = false): Promise<string | null> => {
  try {
    if (!messaging) {
      const initializedMessaging = await initializeFirebaseMessaging();
      if (!initializedMessaging) {
        console.error('[FCM] Messaging not initialized');
        return null;
      }
    }

    // If forceNewToken is true, delete existing token first
    if (forceNewToken) {
      console.log('[FCM] Force new token requested, deleting existing token...');
      await deleteFCMToken(serviceWorkerRegistration);
      // Small delay to ensure token deletion is processed
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log('[FCM] Attempting to get FCM token...');
    const options: { vapidKey: string; serviceWorkerRegistration?: ServiceWorkerRegistration } = {
      vapidKey: VAPID_KEY
    };

    // Only pass serviceWorkerRegistration if we actually have one
    if (serviceWorkerRegistration) {
      options.serviceWorkerRegistration = serviceWorkerRegistration;
      console.log('[FCM] Using service worker for token generation');
    } else {
      console.log('[FCM] Getting token without service worker (foreground notifications only)');
    }

    const token = await getToken(messaging!, options);
    
    if (token) {
      console.log('[FCM] Token retrieved successfully');
    } else {
      console.warn('[FCM] No token returned from Firebase');
    }

    return token || null;
  } catch (error: any) {
    console.error('[FCM] Error getting FCM token:', error);
    console.error('[FCM] Error message:', error?.message);
    console.error('[FCM] Error code:', error?.code);
    
    // If error is about missing service worker, provide helpful message
    if (error?.message?.includes('service worker') || error?.code === 'messaging/unsupported-browser') {
      console.error('[FCM] Service worker is required for FCM token generation in this browser.');
      console.error('[FCM] Please create a service worker file at public/firebase-messaging-sw.js');
    }
    
    return null;
  }
};

/**
 * Setup foreground message listener
 */
export const setupForegroundMessageListener = (callback: (payload: any) => void): (() => void) | null => {
  if (!messaging) {
    console.warn('[FCM] Messaging not initialized. Cannot setup foreground listener.');
    return null;
  }

  try {
    const unsubscribe = onMessage(messaging, callback);
    return unsubscribe;
  } catch (error) {
    console.error('[FCM] Error setting up foreground message listener:', error);
    return null;
  }
};

/**
 * Wait for service worker to be active
 */
export const waitForServiceWorkerActive = async (swUrl?: string): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    console.log('[FCM] Service Worker not supported');
    return null;
  }

  try {
    // First, wait for service worker to be ready
    await navigator.serviceWorker.ready;

    // Get all registrations and find the one we need
    const registrations = await navigator.serviceWorker.getRegistrations();
    let registration: ServiceWorkerRegistration | null = null;

    if (swUrl) {
      // If we have a specific URL, try to find it
      registration = (await navigator.serviceWorker.getRegistration(swUrl)) || null;
    } else {
      // Otherwise, use the first available registration
      registration = registrations.length > 0 ? registrations[0] : null;
    }

    if (!registration) {
      console.log('[FCM] Service worker not registered yet');
      return null;
    }

    // Check if service worker is active
    if (registration.active && registration.active.state === 'activated') {
      console.log('[FCM] Service worker is active');
      return registration;
    }

    // If not active, wait for it to become active
    console.log('[FCM] Service worker registered but not active yet, waiting...');

    return new Promise((resolve) => {
      let checkCount = 0;
      const maxChecks = 50; // 10 seconds max (50 * 200ms)

      const checkInterval = setInterval(() => {
        checkCount++;
        (swUrl 
          ? navigator.serviceWorker.getRegistration(swUrl)
          : Promise.resolve(registrations[0] || null)
        ).then(reg => {
          if (reg && reg.active && reg.active.state === 'activated') {
            console.log('[FCM] Service worker is now active and activated');
            clearInterval(checkInterval);
            resolve(reg);
          } else if (checkCount >= maxChecks) {
            console.log('[FCM] Service worker activation timeout');
            clearInterval(checkInterval);
            resolve(null);
          }
        }).catch(err => {
          console.error('[FCM] Error checking service worker registration:', err);
          if (checkCount >= maxChecks) {
            clearInterval(checkInterval);
            resolve(null);
          }
        });
      }, 200);

      // Also listen for state changes
      if (registration.installing) {
        registration.installing.addEventListener('statechange', () => {
          if (registration.installing!.state === 'activated' && registration.active) {
            clearInterval(checkInterval);
            resolve(registration);
          }
        });
      }

      if (registration.waiting) {
        registration.waiting.addEventListener('statechange', () => {
          if (registration.waiting!.state === 'activated' && registration.active) {
            clearInterval(checkInterval);
            resolve(registration);
          }
        });
      }
    });
  } catch (error) {
    console.error('[FCM] Error waiting for service worker:', error);
    return null;
  }
};

